(() => {
  'use strict';

  const SHELF_SELECTOR = '[data-ksp10-product-shelf]';
  const TAB_SELECTOR = '[data-ksp10-tab]';
  const AJAX_GRID_SELECTOR = '[data-ksp10-ajax-grid]';
  const AJAX_SECTION_ID = 'ks-product-shelf-grid';
  const REQUEST_TIMEOUT_MS = 15000;

  const shelfStates = new WeakMap();
  const deferredShelves = new WeakSet();
  const deferredReviewCarousels = new WeakSet();

  const initializeNearViewport = (
    element,
    callback,
    registry,
    rootMargin = '700px 0px'
  ) => {
    if (
      !element ||
      registry.has(element)
    ) {
      return;
    }

    registry.add(element);

    let started = false;
    let observer = null;

    const interactionEvents = [
      'pointerdown',
      'touchstart',
      'focusin'
    ];

    const cleanup = () => {
      observer?.disconnect();

      interactionEvents.forEach(
        (eventName) => {
          element.removeEventListener(
            eventName,
            start,
            true
          );
        }
      );
    };

    const start = () => {
      if (started) return;

      started = true;
      cleanup();
      callback(element);
    };

    interactionEvents.forEach(
      (eventName) => {
        element.addEventListener(
          eventName,
          start,
          {
            capture: true,
            passive: true
          }
        );
      }
    );

    if (
      !('IntersectionObserver' in window)
    ) {
      start();
      return;
    }

    observer = new IntersectionObserver(
      (entries) => {
        if (
          entries.some(
            (entry) =>
              entry.isIntersecting
          )
        ) {
          start();
        }
      },
      {
        rootMargin,
        threshold: 0.01
      }
    );

    observer.observe(element);
  };

  const numberFrom = (value) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const reviewSourceText = (source) => {
    if (!source) return '';

    const labelledText = Array.from(
      source.querySelectorAll('[aria-label], [title]')
    )
      .map((element) =>
        [
          element.getAttribute('aria-label'),
          element.getAttribute('title')
        ]
          .filter(Boolean)
          .join(' ')
      )
      .join(' ');

    return [
      source.textContent || '',
      source.getAttribute('aria-label') || '',
      source.getAttribute('title') || '',
      labelledText
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
  };

  const reviewSourceData = (source) => {
    const text = reviewSourceText(source);

    const ratingMatch =
      text.match(/([0-5](?:\.\d+)?)\s*\/\s*5/i) ||
      text.match(/([0-5](?:\.\d+)?)\s*out\s*of\s*5/i) ||
      text.match(/rated\s*([0-5](?:\.\d+)?)/i);

    const countMatch = text.match(/(\d[\d,]*)\s*reviews?/i);

    return {
      rating: ratingMatch ? numberFrom(ratingMatch[1]) : null,
      count: countMatch
        ? Number.parseInt(countMatch[1].replace(/,/g, ''), 10)
        : null,
      noReviews: /no reviews|0\s*reviews?/i.test(text)
    };
  };

  const setStarFills = (display, rating) => {
    display
      .querySelectorAll('.ksp10-card__star')
      .forEach((star, index) => {
        const fill = Math.max(
          0,
          Math.min(100, (rating - index) * 100)
        );

        star.style.setProperty(
          '--ksp10-star-fill',
          `${fill}%`
        );
      });
  };

  const renderRating = (holder) => {
    const display = holder.querySelector(
      '.ksp10-card__rating-static'
    );

    const copy = holder.querySelector(
      '.ksp10-card__rating-copy'
    );

    if (!display || !copy) return;

    const sourceData = reviewSourceData(
      holder.querySelector(
        '.ksp10-card__rating-source'
      )
    );

    const fallbackRating = numberFrom(
      holder.dataset.ratingFallback
    );

    const parsedFallbackCount = Number.parseInt(
      holder.dataset.countFallback,
      10
    );

    const fallbackCount = Number.isFinite(
      parsedFallbackCount
    )
      ? parsedFallbackCount
      : null;

    const hasAliRating =
      sourceData.rating !== null ||
      sourceData.noReviews;

    let rating = hasAliRating
      ? sourceData.rating
      : fallbackRating;

    let count = hasAliRating
      ? sourceData.count
      : fallbackCount;

    if (
      rating === null &&
      !sourceData.noReviews
    ) {
      return;
    }

    if (rating === null) {
      rating = 0;
    }

    if (
      count === null &&
      hasAliRating
    ) {
      count = fallbackCount;
    }

    rating = Math.max(
      0,
      Math.min(5, rating)
    );

    const signature = [
      rating.toFixed(3),
      count ?? '',
      sourceData.noReviews
        ? 'empty'
        : 'rated'
    ].join('|');

    if (
      holder.dataset.ratingRendered ===
      signature
    ) {
      return;
    }

    setStarFills(display, rating);

    if (
      sourceData.noReviews &&
      count === null
    ) {
      copy.textContent =
        'No product reviews yet';

      display.setAttribute(
        'aria-label',
        'No product reviews yet'
      );
    } else if (count !== null) {
      const reviewWord =
        count === 1
          ? 'review'
          : 'reviews';

      copy.textContent =
        `${rating.toFixed(1)} · ` +
        `${count.toLocaleString()} ` +
        `${reviewWord}`;

      display.setAttribute(
        'aria-label',
        `Rated ${rating.toFixed(1)} out of 5 from ` +
          `${count.toLocaleString()} product ${reviewWord}`
      );
    } else {
      copy.textContent =
        `${rating.toFixed(1)} out of 5`;

      display.setAttribute(
        'aria-label',
        `Rated ${rating.toFixed(1)} out of 5`
      );
    }

    holder.dataset.ratingRendered =
      signature;

    display.setAttribute(
      'aria-hidden',
      'false'
    );

    display.classList.add('is-ready');
  };

  const renderRatings = (grid) => {
    if (!grid) return;

    grid
      .querySelectorAll(
        '[data-ksp10-rating]'
      )
      .forEach(renderRating);
  };

  const watchRatings = (grid) => {
    if (!grid) {
      return () => {};
    }

    let animationFrame = 0;

    const scheduleRender = () => {
      if (animationFrame) return;

      animationFrame =
        window.requestAnimationFrame(() => {
          animationFrame = 0;
          renderRatings(grid);
        });
    };

    const observer =
      new MutationObserver(
        scheduleRender
      );

    renderRatings(grid);

    observer.observe(grid, {
      childList: true,
      subtree: true,
      characterData: true
    });

    const stopTimer =
      window.setTimeout(() => {
        observer.disconnect();
      }, 10000);

    return () => {
      observer.disconnect();

      window.clearTimeout(
        stopTimer
      );

      if (animationFrame) {
        window.cancelAnimationFrame(
          animationFrame
        );
      }
    };
  };

  const getShelfState = (section) => {
    const currentGrid =
      section.querySelector(
        '[data-ksp10-grid]'
      );

    const existingState =
      shelfStates.get(section);

    if (
      existingState &&
      existingState.grid === currentGrid
    ) {
      return existingState;
    }

    existingState?.stopRatings?.();
    existingState?.controller?.abort();

    window.clearTimeout(
      existingState?.timeoutId
    );

    if (!currentGrid) {
      return null;
    }

    const state = {
      grid: currentGrid,
      initialMarkup:
        currentGrid.innerHTML,
      activeKey: 'all',
      cache: new Map(),
      controller: null,
      timeoutId: 0,
      requestToken: 0,
      stopRatings:
        watchRatings(currentGrid)
    };

    shelfStates.set(
      section,
      state
    );

    return state;
  };

  const announce = (
    section,
    message
  ) => {
    const status =
      section.querySelector(
        '[data-ksp10-status]'
      );

    if (status) {
      status.textContent = message;
    }
  };

  const setLoading = (
    section,
    loading,
    selectedTab = null
  ) => {
    const grid =
      section.querySelector(
        '[data-ksp10-grid]'
      );

    section.classList.toggle(
      'is-loading',
      loading
    );

    grid?.setAttribute(
      'aria-busy',
      String(loading)
    );

    section
      .querySelectorAll(TAB_SELECTOR)
      .forEach((tab) => {
        const isSelectedLoading =
          loading &&
          tab === selectedTab;

        tab.classList.toggle(
          'is-loading',
          isSelectedLoading
        );

        tab.setAttribute(
          'aria-disabled',
          String(isSelectedLoading)
        );
      });
  };

  const activateTab = (
    section,
    activeTab
  ) => {
    section
      .querySelectorAll(TAB_SELECTOR)
      .forEach((tab) => {
        const isActive =
          tab === activeTab;

        tab.classList.toggle(
          'is-active',
          isActive
        );

        if (isActive) {
          tab.setAttribute(
            'aria-current',
            'page'
          );
        } else {
          tab.removeAttribute(
            'aria-current'
          );
        }
      });

    const footer =
      section.querySelector(
        '[data-ksp10-footer]'
      );

    const button =
      section.querySelector(
        '[data-ksp10-button]'
      );

    if (!footer || !button) {
      return;
    }

    const label = (
      activeTab.dataset.buttonLabel ||
      ''
    ).trim();

    const url = (
      activeTab.dataset.buttonUrl ||
      ''
    ).trim();

    footer.hidden = !label;

    if (label) {
      button.textContent = label;
    }

    if (url) {
      button.href = url;
    }
  };

  const parseGridMarkup = (
    responseText
  ) => {
    const responseDocument =
      new DOMParser().parseFromString(
        responseText,
        'text/html'
      );

    const returnedGrid =
      responseDocument.querySelector(
        AJAX_GRID_SELECTOR
      );

    const markup =
      returnedGrid?.innerHTML?.trim() ||
      '';

    if (!markup) {
      throw new Error(
        `The ${AJAX_SECTION_ID} response did not contain ` +
          `${AJAX_GRID_SELECTOR}.`
      );
    }

    const validationTemplate =
      document.createElement(
        'template'
      );

    validationTemplate.innerHTML =
      markup;

    if (
      !validationTemplate.content.querySelector(
        '.ksp10-grid__item, .ksp10-empty'
      )
    ) {
      throw new Error(
        'The returned product shelf contained no usable markup.'
      );
    }

    return markup;
  };

  const buildRequestUrl = (
    fetchUrl
  ) => {
    const requestUrl = new URL(
      fetchUrl,
      window.location.origin
    );

    requestUrl.searchParams.delete(
      'sections'
    );

    requestUrl.searchParams.set(
      'section_id',
      AJAX_SECTION_ID
    );

    return requestUrl;
  };

  const fetchCategoryMarkup = async (
    fetchUrl,
    signal
  ) => {
    const requestUrl =
      buildRequestUrl(fetchUrl);

    const response = await fetch(
      requestUrl.toString(),
      {
        method: 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'text/html',
          'X-Requested-With':
            'XMLHttpRequest'
        },
        signal
      }
    );

    if (!response.ok) {
      throw new Error(
        `Product shelf request failed with HTTP ${response.status}: ` +
          requestUrl.pathname
      );
    }

    return parseGridMarkup(
      await response.text()
    );
  };

  const replaceGrid = (
    section,
    state,
    markup,
    key
  ) => {
    state.stopRatings?.();

    state.grid.innerHTML =
      markup;

    state.activeKey = key;

    state.stopRatings =
      watchRatings(state.grid);

    section.dispatchEvent(
      new CustomEvent(
        'ksp10:products:loaded',
        {
          bubbles: true,
          detail: {
            key,
            grid: state.grid
          }
        }
      )
    );
  };

  const handleTabClick = async (
    event
  ) => {
    const tab =
      event.target.closest?.(
        TAB_SELECTOR
      );

    if (!tab) return;

    const section =
      tab.closest(
        SHELF_SELECTOR
      );

    if (!section) return;

    event.preventDefault();
    event.stopPropagation();

    if (
      tab.getAttribute(
        'aria-disabled'
      ) === 'true'
    ) {
      return;
    }

    const state =
      getShelfState(section);

    if (!state) {
      console.error(
        '[Kawaii Stop product shelf] Missing [data-ksp10-grid].'
      );

      return;
    }

    const key =
      tab.dataset.key ||
      'all';

    if (
      state.activeKey === key &&
      tab.classList.contains(
        'is-active'
      )
    ) {
      return;
    }

    state.controller?.abort();

    window.clearTimeout(
      state.timeoutId
    );

    state.requestToken += 1;

    const requestToken =
      state.requestToken;

    if (key === 'all') {
      replaceGrid(
        section,
        state,
        state.initialMarkup,
        key
      );

      activateTab(
        section,
        tab
      );

      setLoading(
        section,
        false
      );

      announce(
        section,
        `${tab.textContent.trim()} products loaded.`
      );

      return;
    }

    if (
      state.cache.has(key)
    ) {
      replaceGrid(
        section,
        state,
        state.cache.get(key),
        key
      );

      activateTab(
        section,
        tab
      );

      setLoading(
        section,
        false
      );

      announce(
        section,
        `${tab.textContent.trim()} products loaded.`
      );

      return;
    }

    const fetchUrl = (
      tab.dataset.fetchUrl ||
      ''
    ).trim();

    if (!fetchUrl) {
      console.error(
        '[Kawaii Stop product shelf] Missing data-fetch-url.',
        tab
      );

      announce(
        section,
        'This product category is not configured.'
      );

      return;
    }

    setLoading(
      section,
      true,
      tab
    );

    announce(
      section,
      `Loading ${tab.textContent.trim()} products…`
    );

    state.controller =
      new AbortController();

    state.timeoutId =
      window.setTimeout(() => {
        state.controller?.abort();
      }, REQUEST_TIMEOUT_MS);

    try {
      const markup =
        await fetchCategoryMarkup(
          fetchUrl,
          state.controller.signal
        );

      if (
        requestToken !==
        state.requestToken
      ) {
        return;
      }

      state.cache.set(
        key,
        markup
      );

      replaceGrid(
        section,
        state,
        markup,
        key
      );

      activateTab(
        section,
        tab
      );

      announce(
        section,
        `${tab.textContent.trim()} products loaded.`
      );
    } catch (error) {
      if (
        requestToken !==
        state.requestToken
      ) {
        return;
      }

      if (
        error?.name ===
        'AbortError'
      ) {
        console.error(
          '[Kawaii Stop product shelf] Request timed out or was replaced.',
          {
            key,
            fetchUrl
          }
        );
      } else {
        console.error(
          '[Kawaii Stop product shelf]',
          error,
          {
            key,
            fetchUrl
          }
        );
      }

      announce(
        section,
        `We could not load ${tab.textContent.trim()} products. Please try again.`
      );
    } finally {
      if (
        requestToken ===
        state.requestToken
      ) {
        state.controller = null;

        window.clearTimeout(
          state.timeoutId
        );

        state.timeoutId = 0;

        setLoading(
          section,
          false
        );
      }
    }
  };

  const initReviewCarousel = (
    root
  ) => {
    if (
      !root ||
      root.dataset
        .reviewCarouselReady ===
        'true'
    ) {
      return;
    }

    const viewport =
      root.querySelector(
        '[data-review-viewport]'
      );

    const previous =
      root.querySelector(
        '[data-review-previous]'
      );

    const next =
      root.querySelector(
        '[data-review-next]'
      );

    if (
      !viewport ||
      !previous ||
      !next
    ) {
      return;
    }

    root.dataset
      .reviewCarouselReady =
      'true';

    const reducedMotion =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );

    const move = (
      direction
    ) => {
      const maxScroll =
        Math.max(
          0,
          viewport.scrollWidth -
            viewport.clientWidth
        );

      const atStart =
        viewport.scrollLeft <= 4;

      const atEnd =
        viewport.scrollLeft >=
        maxScroll - 4;

      const behavior =
        reducedMotion.matches
          ? 'auto'
          : 'smooth';

      if (
        direction > 0 &&
        atEnd
      ) {
        viewport.scrollTo({
          left: 0,
          behavior
        });
      } else if (
        direction < 0 &&
        atStart
      ) {
        viewport.scrollTo({
          left: maxScroll,
          behavior
        });
      } else {
        viewport.scrollBy({
          left:
            direction *
            viewport.clientWidth,
          behavior
        });
      }
    };

    previous.addEventListener(
      'click',
      () => move(-1)
    );

    next.addEventListener(
      'click',
      () => move(1)
    );
  };

  const initialize = (
    root = document
  ) => {
    const shelves =
      root.matches?.(
        SHELF_SELECTOR
      )
        ? [root]
        : Array.from(
            root.querySelectorAll?.(
              SHELF_SELECTOR
            ) || []
          );

    shelves.forEach(
      (section) => {
        initializeNearViewport(
          section,
          getShelfState,
          deferredShelves
        );
      }
    );

    const carousels =
      root.matches?.(
        '[data-review-carousel]'
      )
        ? [root]
        : Array.from(
            root.querySelectorAll?.(
              '[data-review-carousel]'
            ) || []
          );

    carousels.forEach(
      (carousel) => {
        initializeNearViewport(
          carousel,
          initReviewCarousel,
          deferredReviewCarousels,
          '800px 0px'
        );
      }
    );
  };

  if (
    window
      .__KS_HOME_TAB_CLICK_HANDLER__
  ) {
    document.removeEventListener(
      'click',
      window
        .__KS_HOME_TAB_CLICK_HANDLER__,
      true
    );
  }

  window
    .__KS_HOME_TAB_CLICK_HANDLER__ =
    handleTabClick;

  document.addEventListener(
    'click',
    handleTabClick,
    true
  );

  if (
    window
      .__KS_HOME_SECTION_LOAD_HANDLER__
  ) {
    document.removeEventListener(
      'shopify:section:load',
      window
        .__KS_HOME_SECTION_LOAD_HANDLER__
    );
  }

  window
    .__KS_HOME_SECTION_LOAD_HANDLER__ =
    (event) => {
      initialize(event.target);
    };

  document.addEventListener(
    'shopify:section:load',
    window
      .__KS_HOME_SECTION_LOAD_HANDLER__
  );

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(document),
      {
        once: true
      }
    );
  } else {
    initialize(document);
  }
})();
